/**
 * Game Pass option derivation (single source of truth).
 *
 * The Create Season / Create Task / Create Challenge surfaces render their
 * dropdowns from the backend catalogue (`GET /config` + `GET /task-options`).
 * These pure helpers translate the API payloads into the UI option shapes the
 * components already consume, and fall back to the bundled static catalogues
 * (`createSeasonData.ts`) whenever a field is absent / empty — so the editors
 * keep working while the catalogue is loading, errored, or not yet shipped by
 * the backend.
 *
 * Kept framework-free (no hooks) so the mapping logic can be unit-tested in
 * isolation, mirroring the convention used by the other `game-pass` mappers.
 */

import { AppStrings } from "../../utils/constants/AppStrings";
import {
  BET_CURRENCY_OPTIONS,
  BET_TYPE_OPTIONS,
  CASINO_PROVIDER_OPTIONS,
  MONEY_TYPE_TO_BET_CURRENCY,
  REWARD_TYPE_OPTIONS,
  SOCIAL_EVENT_TYPE_TO_SOCIAL_TASK_TYPE,
  SOCIAL_TASK_TYPE_LABEL_KEY,
  SOCIAL_TASK_TYPE_OPTIONS,
  SPORT_SPECIFIC_OPTIONS,
  TASK_QUALIFIER_OPTIONS,
  TASK_TYPE_OPTIONS,
  type BetCurrency,
  type BetType,
  type CasinoProviderOption,
  type RewardType,
  type SocialTaskType,
  type SportOption,
  type TaskQualifier,
  type TaskType,
} from "../../components/GamePass/CreateSeason/createSeasonData";
import {
  CHALLENGE_CURRENCY_TO_REWARD_TYPE,
  REWARD_TYPE_TO_CHALLENGE_CURRENCY,
} from "./challengeRewards";
import type {
  ChallengeRewardCurrency,
  GamePassBonusRewardParam,
  GamePassConfigResponse,
  TaskOptionLeague,
  TaskOptionsData,
} from "../../types/gamePass";

/** A `{ value, label }` pair the Select / pill components render. */
export type LabeledOption<V extends string> = {
  value: V;
  label: string;
};

/** Title-case a snake_case backend id (e.g. `american_football` → "American
 *  Football", `sa_gaming` → "Sa Gaming") for display. Used for the sport /
 *  casino-provider catalogues, whose API entries carry only a machine name. */
const humanizeId = (id: string): string =>
  id
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

/** Build a `value → label` lookup from a static option array that carries a
 *  `labelKey` into `AppStrings`. */
const buildLabelMap = <V extends string>(
  options: ReadonlyArray<{ value: V; labelKey: keyof typeof AppStrings }>,
): Map<V, string> =>
  new Map(options.map((option) => [option.value, AppStrings[option.labelKey]]));

/** Map a static option array to the rendered `{ value, label }` shape. */
const toLabeledOptions = <V extends string>(
  options: ReadonlyArray<{ value: V; labelKey: keyof typeof AppStrings }>,
): LabeledOption<V>[] =>
  options.map((option) => ({
    value: option.value,
    label: AppStrings[option.labelKey],
  }));

/** Strip a trailing parenthetical (e.g. " (Free path)") from a reward label so
 *  the currency name reads cleanly in the dropdown. */
const stripPathSuffix = (label: string): string =>
  label.replace(/\s*\([^)]*\)\s*$/, "").trim();

// ─── Challenge reward dropdown (reward_types + bonus_reward_types merged) ────

/**
 * A challenge reward dropdown option. Carries everything the editor needs to
 * both render the row and build the wire payload, discriminated by `kind`:
 *   - `currency` rewards (from `config.reward_types`) ship `{ amount, currency,
 *     tier }` where `currency` is `play` / `value`.
 *   - `bonus` rewards (from `config.bonus_reward_types`) ship `{ amount,
 *     currency, tier, reward_type, params }` and surface `params` as inputs.
 */
export type ChallengeRewardOption = LabeledOption<string> & {
  kind: "currency" | "bonus";
  /** Currency rewards — the BE `play` / `value` money type + the UI RewardType
   *  (which equals `value`). */
  currency?: ChallengeRewardCurrency;
  rewardType?: RewardType;
  /** Bonus rewards — the BE grant identifier (e.g. `free_cash_pick`). */
  bonusRewardType?: string;
  /** Bonus rewards — the BE bonus category (e.g. `free_pick`). */
  bonusCurrency?: string;
  /** Bonus rewards — label for the amount input (e.g. "Boost percentage"). */
  amountLabel?: string;
  /** Bonus rewards — the per-bonus tunables surfaced as inputs. */
  params?: GamePassBonusRewardParam[];
};

/** Currency reward options: the distinct currencies in `config.reward_types`
 *  (the tier is chosen by the challenge's Free/Premium tab, not the reward
 *  row), mapped onto the canonical UI reward type for each currency (`play` →
 *  Rebet Coin, `value` → Rebet Cash). Labels prefer `config.currencies[*].name`,
 *  then the reward label with its `(… path)` suffix stripped. */
const buildCurrencyRewardOptions = (
  config?: GamePassConfigResponse | null,
): ChallengeRewardOption[] => {
  const rewardTypes = config?.reward_types;
  if (!Array.isArray(rewardTypes) || rewardTypes.length === 0) return [];
  const seen = new Set<ChallengeRewardCurrency>();
  const options: ChallengeRewardOption[] = [];
  for (const rewardType of rewardTypes) {
    const currency = rewardType?.currency;
    if (currency !== "play" && currency !== "value") continue;
    if (seen.has(currency)) continue;
    seen.add(currency);
    const value = CHALLENGE_CURRENCY_TO_REWARD_TYPE[currency];
    const label =
      config?.currencies?.[currency]?.name?.trim() ||
      (typeof rewardType.label === "string"
        ? stripPathSuffix(rewardType.label)
        : "") ||
      value;
    options.push({ value, label, kind: "currency", currency, rewardType: value });
  }
  return options;
};

/** Bonus reward options from `config.bonus_reward_types`. Entries that are
 *  unavailable (`available: false`) or carry no `reward_type` (e.g. Free Spin,
 *  pending bonus-engine support) are skipped so the picker only offers grants
 *  the BE can actually action. */
const buildBonusRewardOptions = (
  config?: GamePassConfigResponse | null,
): ChallengeRewardOption[] => {
  const bonusRewardTypes = config?.bonus_reward_types;
  if (!Array.isArray(bonusRewardTypes) || bonusRewardTypes.length === 0) {
    return [];
  }
  const seen = new Set<string>();
  const options: ChallengeRewardOption[] = [];
  for (const bonus of bonusRewardTypes) {
    if (!bonus || bonus.available === false) continue;
    const rewardType =
      typeof bonus.reward_type === "string" ? bonus.reward_type.trim() : "";
    if (!rewardType || seen.has(rewardType)) continue;
    seen.add(rewardType);
    const label =
      (typeof bonus.label === "string" && bonus.label.trim()) || rewardType;
    const bonusCurrency =
      typeof bonus.currency === "string" && bonus.currency.trim()
        ? bonus.currency.trim()
        : rewardType;
    options.push({
      value: rewardType,
      label,
      kind: "bonus",
      bonusRewardType: rewardType,
      bonusCurrency,
      amountLabel:
        typeof bonus.amount_label === "string" && bonus.amount_label.trim()
          ? bonus.amount_label.trim()
          : undefined,
      params: Array.isArray(bonus.params)
        ? bonus.params.filter(
            (param): param is GamePassBonusRewardParam =>
              Boolean(param) && typeof param.name === "string" && param.name !== "",
          )
        : [],
    });
  }
  return options;
};

/** Challenge "Rewards" dropdown options — the currency rewards
 *  (`config.reward_types`) and the available bonus rewards
 *  (`config.bonus_reward_types`) merged into one list. Either source may be
 *  absent/empty and the other still populates the picker; only when BOTH are
 *  absent does the full static reward catalogue fill in (so the editor keeps
 *  working while the config loads / is errored / not yet shipped). */
export const getRewardTypeOptions = (
  config?: GamePassConfigResponse | null,
): ChallengeRewardOption[] => {
  const merged = [
    ...buildCurrencyRewardOptions(config),
    ...buildBonusRewardOptions(config),
  ];
  if (merged.length > 0) return merged;
  return REWARD_TYPE_OPTIONS.map((option) => ({
    value: option.value,
    label: AppStrings[option.labelKey],
    kind: "currency" as const,
    currency: REWARD_TYPE_TO_CHALLENGE_CURRENCY[option.value],
    rewardType: option.value,
  }));
};

/** Currency-only reward options for surfaces that must NOT offer bonus rewards
 *  while the config is still loading. Driven purely by `config.reward_types`
 *  (Rebet Coin / Rebet Cash), never the bonus catalogue. Falls back to the two
 *  platform currencies — and nothing else (no XP / USD / bonus) — while the
 *  config loads or is absent, so the picker stays fully config-shaped. Monthly
 *  tiers use this only as that loading fallback; once `GET /config` arrives
 *  they switch to {@link getRewardTypeOptions} so bonus types (Deposit Match,
 *  Free Pick, Profit Boost) appear alongside the currency rewards. */
export const getCurrencyRewardTypeOptions = (
  config?: GamePassConfigResponse | null,
): ChallengeRewardOption[] => {
  const options = buildCurrencyRewardOptions(config);
  if (options.length > 0) return options;
  return (["play", "value"] as const).map((currency) => ({
    value: CHALLENGE_CURRENCY_TO_REWARD_TYPE[currency],
    label:
      currency === "value"
        ? AppStrings.REWARD_TYPE_REBET_CASH
        : AppStrings.REWARD_TYPE_REBET_COINS,
    kind: "currency" as const,
    currency,
    rewardType: CHALLENGE_CURRENCY_TO_REWARD_TYPE[currency],
  }));
};

// ─── Task "Bet Currency" dropdown (config.currencies) ───────────────────────

/**
 * Task "Bet Currency" multi-select options. Built from `config.currencies`
 * (`play` / `value`), mapping each entry's `money_type` onto the UI
 * `BetCurrency` pill and using the entry's `name` as the label. Falls back to
 * the static bet-currency catalogue when the config carries no currencies.
 */
export const getBetCurrencyOptions = (
  config?: GamePassConfigResponse | null,
): LabeledOption<BetCurrency>[] => {
  const currencies = config?.currencies;
  if (currencies) {
    const options: LabeledOption<BetCurrency>[] = [];
    (["play", "value"] as const).forEach((key) => {
      const currency = currencies[key];
      const moneyType = currency?.money_type;
      if (!moneyType) return;
      const value = MONEY_TYPE_TO_BET_CURRENCY[moneyType];
      if (!value) return;
      options.push({ value, label: currency?.name?.trim() || value });
    });
    if (options.length > 0) return options;
  }
  return toLabeledOptions(BET_CURRENCY_OPTIONS);
};

/**
 * Per-currency minimum bet amounts for the Task "Minimum Bet Amount" gate,
 * built from `config.currencies[*].min_bet` and keyed by the UI `BetCurrency`
 * pill (so the modal can enforce, e.g., a 100-coin floor when Rebet Coins is
 * selected). Entries without a positive numeric `min_bet` are omitted, leaving
 * those currencies unconstrained. Returns an empty map when the config carries
 * no currencies (no floor is enforced while the catalogue is loading / absent).
 */
export const getBetCurrencyMinimums = (
  config?: GamePassConfigResponse | null,
): Partial<Record<BetCurrency, number>> => {
  const minimums: Partial<Record<BetCurrency, number>> = {};
  const currencies = config?.currencies;
  if (!currencies) return minimums;
  (["play", "value"] as const).forEach((key) => {
    const currency = currencies[key];
    const moneyType = currency?.money_type;
    if (!moneyType) return;
    const value = MONEY_TYPE_TO_BET_CURRENCY[moneyType];
    if (!value) return;
    const minBet = currency?.min_bet;
    if (typeof minBet === "number" && Number.isFinite(minBet) && minBet > 0) {
      minimums[value] = minBet;
    }
  });
  return minimums;
};

// ─── Task Type pills (task-options.data_sources) ────────────────────────────

/** BE `data_source` enum value → UI Task Type pill. The socials wire value is
 *  the singular `SOCIAL`; the pill is `SOCIALS`. */
const DATA_SOURCE_TO_TASK_TYPE: Record<string, TaskType> = {
  SPORTSBOOK: "SPORTSBOOK",
  SOCIAL: "SOCIALS",
  CASINO: "CASINO",
};

/**
 * Task Type pill options, derived from `task-options.data_sources` in API
 * order (mapping the singular `SOCIAL` wire value onto the `SOCIALS` pill).
 * Unknown data sources are skipped; falls back to the static catalogue when
 * none are recognised.
 */
export const getTaskTypeOptions = (
  taskOptions?: TaskOptionsData | null,
): LabeledOption<TaskType>[] => {
  const labels = buildLabelMap(TASK_TYPE_OPTIONS);
  const sources = taskOptions?.data_sources;
  if (Array.isArray(sources) && sources.length > 0) {
    const seen = new Set<TaskType>();
    const options: LabeledOption<TaskType>[] = [];
    for (const source of sources) {
      const value = DATA_SOURCE_TO_TASK_TYPE[source];
      const label = value ? labels.get(value) : undefined;
      if (!value || !label || seen.has(value)) continue;
      seen.add(value);
      options.push({ value, label });
    }
    if (options.length > 0) return options;
  }
  return toLabeledOptions(TASK_TYPE_OPTIONS);
};

// ─── Bet Type pills (task-options.bet_types) ────────────────────────────────

/**
 * Type-of-Bet pill options, derived from `task-options.bet_types` in API
 * order. Each value must match a known `BetType` (so an unrecognised BE enum
 * never renders an unlabelled pill); falls back to the static catalogue when
 * none match.
 */
export const getBetTypeOptions = (
  taskOptions?: TaskOptionsData | null,
): LabeledOption<BetType>[] => {
  const labels = buildLabelMap(BET_TYPE_OPTIONS);
  const apiTypes = taskOptions?.bet_types;
  if (Array.isArray(apiTypes) && apiTypes.length > 0) {
    const options: LabeledOption<BetType>[] = [];
    for (const apiType of apiTypes) {
      const value = apiType as BetType;
      const label = labels.get(value);
      if (label) options.push({ value, label });
    }
    if (options.length > 0) return options;
  }
  return toLabeledOptions(BET_TYPE_OPTIONS);
};

// ─── Qualifier pills (task-options.outcomes) ────────────────────────────────

/**
 * Qualifier pill options, derived from `task-options.outcomes` in API order.
 * Each value must match a known `TaskQualifier`; falls back to the static
 * catalogue when none match.
 */
export const getQualifierOptions = (
  taskOptions?: TaskOptionsData | null,
): LabeledOption<TaskQualifier>[] => {
  const labels = buildLabelMap(TASK_QUALIFIER_OPTIONS);
  const outcomes = taskOptions?.outcomes;
  if (Array.isArray(outcomes) && outcomes.length > 0) {
    const options: LabeledOption<TaskQualifier>[] = [];
    for (const outcome of outcomes) {
      const value = outcome as TaskQualifier;
      const label = labels.get(value);
      if (label) options.push({ value, label });
    }
    if (options.length > 0) return options;
  }
  return toLabeledOptions(TASK_QUALIFIER_OPTIONS);
};

// ─── Social Task Type dropdown (task-options.social_event_types) ─────────────

/**
 * Socials "Task Type" dropdown options, derived from
 * `task-options.social_event_types`. Each backend event is mapped onto the UI
 * `SocialTaskType` that carries its rule semantics (so the rule encoding /
 * round-trip stays keyed on the stable internal enum); events with no UI
 * counterpart are skipped. Falls back to the static catalogue when none map.
 */
export const getSocialTaskTypeOptions = (
  taskOptions?: TaskOptionsData | null,
): LabeledOption<SocialTaskType>[] => {
  const events = taskOptions?.social_event_types;
  if (Array.isArray(events) && events.length > 0) {
    const seen = new Set<SocialTaskType>();
    const options: LabeledOption<SocialTaskType>[] = [];
    for (const event of events) {
      const value = SOCIAL_EVENT_TYPE_TO_SOCIAL_TASK_TYPE[event];
      if (!value || seen.has(value)) continue;
      seen.add(value);
      options.push({ value, label: AppStrings[SOCIAL_TASK_TYPE_LABEL_KEY[value]] });
    }
    if (options.length > 0) return options;
  }
  return toLabeledOptions(SOCIAL_TASK_TYPE_OPTIONS);
};

// ─── Sport Specific picker (task-options.sports) ────────────────────────────

/** Normalise a sport name / id / leagues key for matching. The leagues record
 *  is usually keyed by sport `name`, but some payloads use different separators
 *  or the sport id/value instead, so collapse punctuation and lowercase. */
const normalizeSportKey = (key: string): string =>
  key.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

/** Picker copy for a task-options league. Prefer the BE `label`
 *  (`"Name (Country)"`); otherwise append a non-empty `country`; otherwise
 *  keep `name` so legacy payloads still render. */
const getLeaguePickerLabel = (league: TaskOptionLeague): string => {
  const fromApi = league.label?.trim();
  if (fromApi) return fromApi;
  const country = league.country?.trim();
  if (country) return `${league.name} (${country})`;
  return league.name;
};

/**
 * Sport Specific picker catalogue, derived from `task-options.sports` with the
 * leagues nested under each sport from `task-options.leagues` (keyed by sport
 * `name`, matched case/separator-insensitively). The sport's `value` ships as
 * the task's `sport_id` rule; each league's `tournament_id` ships as the
 * `league_id` rule (falls back to the league name when no id is present).
 * Falls back to the static catalogue when no sports are present.
 */
export const getSportOptions = (
  taskOptions?: TaskOptionsData | null,
): SportOption[] => {
  const sports = taskOptions?.sports;
  if (Array.isArray(sports) && sports.length > 0) {
    // Re-key the leagues record by the normalised sport name so hyphen /
    // underscore / casing mismatches still resolve.
    const leaguesBySport = new Map<string, TaskOptionLeague[]>();
    const rawLeagues = taskOptions?.leagues;
    if (rawLeagues && typeof rawLeagues === "object") {
      for (const [sportName, leagues] of Object.entries(rawLeagues)) {
        if (Array.isArray(leagues)) {
          leaguesBySport.set(normalizeSportKey(sportName), leagues);
        }
      }
    }
    const options = sports
      .filter((sport) => typeof sport?.value === "string" && sport.value !== "")
      .map((sport) => {
        const leagues =
          (sport.name ? leaguesBySport.get(normalizeSportKey(sport.name)) : undefined) ??
          leaguesBySport.get(normalizeSportKey(sport.value)) ??
          (sport.id ? leaguesBySport.get(normalizeSportKey(sport.id)) : undefined) ??
          [];
        return {
          value: sport.value,
          label: sport.name ? humanizeId(sport.name) : sport.value,
          leagues: leagues
            .filter(
              (league) =>
                typeof league?.name === "string" && league.name !== "",
            )
            .map((league) => ({
              value: league.tournament_id ?? league.name,
              label: getLeaguePickerLabel(league),
              tournamentId: league.tournament_id ?? undefined,
            })),
        };
      });
    if (options.length > 0) return options;
  }
  return [...SPORT_SPECIFIC_OPTIONS];
};

// ─── Casino provider picker (task-options.casino_providers) ─────────────────

/**
 * Casino provider picker catalogue, derived from
 * `task-options.casino_providers`. The API only ships provider names (no
 * games), so each provider carries an empty `games` list and the picker
 * renders as a flat provider checkbox list. The provider id is sent verbatim
 * as the task's `provider` rule. Falls back to the static catalogue when no
 * providers are present.
 */
export const getCasinoProviderOptions = (
  taskOptions?: TaskOptionsData | null,
): CasinoProviderOption[] => {
  const providers = taskOptions?.casino_providers;
  if (Array.isArray(providers) && providers.length > 0) {
    const options = providers
      .filter((provider) => typeof provider === "string" && provider !== "")
      .map((provider) => ({
        value: provider,
        label: humanizeId(provider),
        games: [],
      }));
    if (options.length > 0) return options;
  }
  return [...CASINO_PROVIDER_OPTIONS];
};
